import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Storefront, Handshake, ShieldCheck, EnvelopeSimple, MapPin, CheckCircle, FileXls, Camera, VideoCamera } from "@phosphor-icons/react";
import { SUPPORT_EMAIL } from "@/lib/config";

const I_AM_A = ["Seller", "Buyer", "Dealership", "Distributor", "Retailer", "Exporter", "Other"];
const REASONS = [
  "I Want to Sell Stock in Bulk",
  "I Want to Buy a Lot",
  "Dealership Closure / Complete Stock Liquidation",
  "Existing Lot / Transaction Support",
  "Escrow Query",
  "General Enquiry",
  "Other",
];

const empty = { name: "", company: "", email: "", mobile: "", i_am_a: "", reason: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Contact CarPartsAuction.in | Bulk Car Parts Buying & Stock Liquidation"; }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.mobile) { toast.error("Please fill all required fields"); return; }
    if (!form.i_am_a) { toast.error("Please select the 'I am a' option"); return; }
    if (!form.reason) { toast.error("Please select a reason for contact"); return; }
    if (!form.message) { toast.error("Please add a message"); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Enquiry submitted. Our team will be in touch shortly.");
      setForm(empty);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-navy text-white bp-grid">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
          <h1 className="font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">Get in touch with <span className="text-brand">CarPartsAuction.in</span></h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/70">
            Whether you want to sell car spare-parts inventory in bulk, explore available lots, or need assistance
            with a transaction, our team is here to help. CarPartsAuction.in is a B2B marketplace operated by Iradium
            Automobiles Private Limited — the company behind SparesHub.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-[1fr_1.15fr] lg:px-8 lg:py-20">
        {/* Left: context + contact info */}
        <div className="space-y-8">
          <div className="rounded-sm border border-border bg-white p-6">
            <div className="flex items-center gap-3">
              <Storefront weight="fill" size={22} className="text-brand" />
              <h2 className="font-display text-xl font-bold text-navy">For sellers</h2>
            </div>
            <p className="mt-3 text-sm text-subtext">
              Have dead, slow-moving, surplus or dealership closure stock? List your inventory on CarPartsAuction.in
              and reach relevant B2B bulk buyers. To prepare your lot, keep the following ready:
            </p>
            <ul className="mt-4 space-y-2 text-sm text-ink">
              <li className="flex items-center gap-2"><FileXls size={18} className="text-teal" /> Stock list in Excel format</li>
              <li className="flex items-center gap-2"><Camera size={18} className="text-teal" /> 6 clear photographs of the inventory</li>
              <li className="flex items-center gap-2"><VideoCamera size={18} className="text-teal" /> 1 short walkthrough video of the stock or warehouse</li>
            </ul>
            <p className="mt-4 text-sm text-subtext">
              We also provide additional support for large inventory liquidation arising from dealership closure,
              restructuring or brand change.
            </p>
            <Button asChild className="mt-5 rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="contact-list-stock">
              <Link to="/register?role=seller">List Your Stock</Link>
            </Button>
          </div>

          <div className="rounded-sm border border-border bg-white p-6">
            <div className="flex items-center gap-3">
              <Handshake weight="fill" size={22} className="text-darkteal" />
              <h2 className="font-display text-xl font-bold text-navy">For buyers</h2>
            </div>
            <p className="mt-3 text-sm text-subtext">
              Interested in purchasing automobile spare-parts inventory in bulk? Browse available lots, review
              inventory details, submit your Expression of Interest and request an inspection where available.
            </p>
            <p className="mt-3 flex items-start gap-2 text-sm text-subtext">
              <ShieldCheck weight="fill" size={18} className="mt-0.5 shrink-0 text-darkteal" />
              Eligible transactions may also be supported through Escrow for greater transaction confidence.
            </p>
            <Button asChild variant="outline" className="mt-5 rounded-sm border-navy/20 text-navy hover:bg-navy hover:text-white" data-testid="contact-browse-lots">
              <Link to="/lots">Browse Lots</Link>
            </Button>
          </div>

          <div className="rounded-sm border border-border bg-navy-50 p-6">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-subtext">Contact information</h3>
            <p className="mt-3 font-display text-lg font-bold text-navy">Iradium Automobiles Private Limited</p>
            <p className="mt-1 flex items-center gap-2 text-sm text-subtext"><MapPin size={16} className="text-brand" /> Pune, Maharashtra, India</p>
            <p className="mt-1 flex items-center gap-2 text-sm text-subtext"><EnvelopeSimple size={16} className="text-brand" /> <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-brand hover:underline">{SUPPORT_EMAIL}</a></p>
          </div>
        </div>

        {/* Right: form */}
        <div className="rounded-sm border border-border bg-white p-6 lg:p-8" data-testid="contact-form-card">
          <h2 className="font-display text-2xl font-black tracking-tight text-navy">Send us an enquiry</h2>
          <p className="mt-1 text-sm text-subtext">Fields marked * are required.</p>

          {sent ? (
            <div className="mt-8 flex flex-col items-center rounded-sm border border-teal/30 bg-teal/5 p-8 text-center" data-testid="contact-success">
              <CheckCircle weight="fill" size={48} className="text-teal" />
              <h3 className="mt-4 font-display text-xl font-bold text-navy">Thank you — your enquiry is in.</h3>
              <p className="mt-2 text-sm text-subtext">Our team will review your message and contact you shortly.</p>
              <Button onClick={() => setSent(false)} variant="outline" className="mt-5 rounded-sm" data-testid="contact-new-enquiry">Send another enquiry</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Name *</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-sm" placeholder="Your name" /></div>
                <div><Label>Company Name *</Label><Input data-testid="contact-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="mt-1.5 rounded-sm" placeholder="Company / business name" /></div>
                <div><Label>Email Address *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-sm" placeholder="you@company.com" /></div>
                <div><Label>Mobile Number *</Label><Input data-testid="contact-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5 rounded-sm" placeholder="+91 ..." /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>I am a *</Label>
                  <Select value={form.i_am_a} onValueChange={(v) => set("i_am_a", v)}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid="contact-iama"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{I_AM_A.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reason for Contact *</Label>
                  <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid="contact-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                    <SelectContent>{REASONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} className="mt-1.5 rounded-sm" placeholder="How can we help?" /></div>
              <Button type="submit" disabled={loading} className="h-12 w-full rounded-sm bg-brand text-base font-semibold text-white hover:bg-brand-hover" data-testid="contact-submit">
                {loading ? "Submitting..." : "Submit Enquiry"}
              </Button>
              <p className="text-xs leading-relaxed text-subtext">
                By submitting this form, you agree that CarPartsAuction.in / Iradium Automobiles Private Limited may
                contact you regarding your enquiry. Please review our{" "}
                <Link to="/privacy-policy" className="font-semibold text-brand hover:underline">Privacy Policy</Link>{" "}
                for information about how we handle your data.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
